import React, { useState, useRef, useEffect, useCallback } from 'react';
import { UploadCloud, Play, Trash2, RefreshCw, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { GlassButton } from '../ui/GlassButton';
import { apiClient as api } from '../../services/api';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface Props {
  productId: string;
}

interface VideoStatus {
  video_guid: string | null;
  video_status: number | null;
  video_status_label: string;
}

const STATUS_LABELS: Record<string, string> = {
  no_video: 'No video uploaded',
  created: 'Created',
  uploaded: 'Uploaded — queued for processing',
  processing: 'Processing…',
  transcoding: 'Transcoding…',
  finishing: 'Finishing…',
  ready: 'Ready',
  error: 'Encoding error',
  upload_failed: 'Upload failed',
};

const VideoUploader: React.FC<Props> = ({ productId }) => {
  const [status, setStatus] = useState<VideoStatus | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get(`/video/products/${productId}/video-status`);
      setStatus(res.data);
      // Stop polling when ready or error
      if (res.data.video_status_label === 'ready' || res.data.video_status_label === 'error') {
        stopPolling();
      }
    } catch {
      // ignore
    }
  }, [productId]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(fetchStatus, 5000);
  }, [fetchStatus]);

  useEffect(() => {
    fetchStatus();
    return () => stopPolling();
  }, [fetchStatus]);

  // Start polling when in-progress
  useEffect(() => {
    if (status && status.video_status !== null && status.video_status >= 1 && status.video_status <= 4) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [status?.video_status, startPolling]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const MAX_GB = 4;
    if (file.size > MAX_GB * 1024 * 1024 * 1024) {
      setError(`File must be under ${MAX_GB} GB`);
      return;
    }

    setError('');
    setUploading(true);
    setUploadProgress(0);

    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/video/products/${productId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      });
      await fetchStatus();
      startPolling();
    } catch (err: any) {
      const msg = getApiError(err, 'Upload failed. Check that your Bunny.net account is connected in Video Settings.');
      setError(msg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove the video from this product?')) return;
    setDeleting(true);
    try {
      await api.delete(`/video/products/${productId}/video`);
      setStatus({ video_guid: null, video_status: null, video_status_label: 'no_video' });
    } catch (err: any) {
      setError(getApiError(err, 'Delete failed.'));
    } finally {
      setDeleting(false);
    }
  };

  const isProcessing = status && status.video_status !== null && status.video_status >= 1 && status.video_status <= 4;
  const isReady = status?.video_status_label === 'ready';
  const hasVideo = Boolean(status?.video_guid);

  const statusIcon = () => {
    if (!hasVideo) return null;
    if (isReady) return <CheckCircle size={16} style={{ color: 'var(--success)' }} />;
    if (status?.video_status_label === 'error' || status?.video_status_label === 'upload_failed') {
      return <AlertCircle size={16} style={{ color: 'var(--danger)' }} />;
    }
    return <Loader size={16} className="spin-icon" />;
  };

  return (
    <div className="video-uploader">
      {error && <div className="products-error" style={{ marginBottom: 12 }}>{error}</div>}

      {uploading ? (
        <div className="video-upload-progress">
          <UploadCloud size={28} />
          <div className="video-progress-bar-wrap">
            <div className="video-progress-bar" style={{ width: `${uploadProgress}%` }} />
          </div>
          <span>{uploadProgress}% — uploading to Bunny CDN…</span>
        </div>
      ) : hasVideo ? (
        <div className="video-status-card">
          <div className="video-status-info">
            {statusIcon()}
            <div>
              <div className="video-status-label">
                {STATUS_LABELS[status!.video_status_label] || status!.video_status_label}
              </div>
              <div className="video-guid-label">GUID: {status!.video_guid}</div>
            </div>
          </div>
          <div className="video-status-actions">
            {isProcessing && (
              <GlassButton
                variant="ghost"
                size="sm"
                icon={<RefreshCw size={14} />}
                onClick={fetchStatus}
                title="Refresh status"
              />
            )}
            <GlassButton
              variant="ghost"
              size="sm"
              icon={<UploadCloud size={14} />}
              onClick={() => fileInputRef.current?.click()}
              title="Replace video"
            >
              Replace
            </GlassButton>
            <GlassButton
              variant="ghost"
              size="sm"
              icon={<Trash2 size={14} />}
              onClick={handleDelete}
              loading={deleting}
              title="Remove video"
            />
          </div>
        </div>
      ) : (
        <div
          className="upload-drop-zone"
          onClick={() => fileInputRef.current?.click()}
          style={{ cursor: 'pointer' }}
        >
          <UploadCloud size={36} />
          <p>Click to upload your video</p>
          <span className="upload-hint">MP4, MOV, AVI supported · max 4 GB · hosted on Bunny CDN</span>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default VideoUploader;
